import React, { useEffect, useState } from "react";
import axios from "axios";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import { useParams } from "react-router-dom";
import "../../../assets/css/FaqSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function FaqSection() {
  const { section, id } = useParams();
  const [activeIndex, setActiveIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const faqsPerPage = 10;

  const toggleFAQ = (index) => {
    setActiveIndex((current) => (current === index ? null : index));
  };

  const totalPages = Math.ceil(faqData.length / faqsPerPage);

  const startIndex = (currentPage - 1) * faqsPerPage;

  const currentFAQs = faqData.slice(startIndex, startIndex + faqsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    setActiveIndex(null);
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchFAQData() {
      setLoading(true);
      setHasError(false);

      try {
        await axios.get("sanctum/csrf-cookie");

        const response = await axios.get(
          `${BASE_URL}faqdata?visibility[eq]=publish`,
        );

        if (!isMounted) return;

        if (response.data?.status === 200) {
          const records = Array.isArray(response.data.allData)
            ? response.data.allData
            : [];
          setFaqData(records);
          setActiveIndex(null);
          setCurrentPage(1);
        } else {
          setFaqData([]);
          setHasError(true);
          console.error(
            "Unable to load FAQ data:",
            response.data?.validation_error,
          );
        }
      } catch (error) {
        if (isMounted) {
          setFaqData([]);
          setHasError(true);
        }
        console.error("Error loading FAQ data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchFAQData();

    return () => {
      isMounted = false;
    };
  }, [section, id]);

  return (
    <section
      id="faq"
      className="fmw-faq-section"
      aria-labelledby="fmw-faq-title"
    >
      <div className="fmw-faq-container">
        <header className="fmw-faq-heading">
          <span className="fmw-faq-eyebrow">
            <span className="fmw-faq-eyebrow-line" />
            Help and information
          </span>
          <h2 id="fmw-faq-title">
            Frequently Asked <span>Questions</span>
          </h2>
          {/* <p>
            Find answers to common questions about our services, programmes and
            processes.
          </p> */}
        </header>

        {loading ? (
          <div className="fmw-faq-message" role="status">
            <span className="fmw-faq-spinner" aria-hidden="true" />
            Loading frequently asked questions…
          </div>
        ) : hasError ? (
          <div className="fmw-faq-message fmw-faq-message-error" role="status">
            We couldn’t load the FAQs at the moment. Please try again later.
          </div>
        ) : faqData.length === 0 ? (
          <div className="fmw-faq-message">
            There are no frequently asked questions to display right now.
          </div>
        ) : (
          <div className="fmw-faq-layout">
            <aside className="fmw-faq-aside">
              <div className="fmw-faq-aside-mark" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <p className="fmw-faq-aside-kicker">Need clarification?</p>
              <h3>We’re here to help.</h3>
              <p>
                Browse the questions below to find useful information and
                guidance.
              </p>
              <div className="fmw-faq-aside-rule" />
              <p className="fmw-faq-aside-note">
                Official information from the Ministry.
              </p>
            </aside>

            <div className="fmw-faq-list">
              {currentFAQs.map((item, index) => {
                const isActive = activeIndex === index;
                const answerId = `fmw-faq-answer-${item.id ?? index}`;
                const questionId = `fmw-faq-question-${item.id ?? index}`;

                return (
                  <article
                    className={`fmw-faq-item${isActive ? " is-open" : ""}`}
                    key={item.id ?? index}
                  >
                    <h3 className="fmw-faq-question-heading">
                      <button
                        id={questionId}
                        className="fmw-faq-question"
                        type="button"
                        aria-expanded={isActive}
                        aria-controls={answerId}
                        onClick={() => toggleFAQ(index)}
                      >
                        <span className="fmw-faq-number">
                          {String(startIndex + index + 1).padStart(2, "0")}
                        </span>
                        <span className="fmw-faq-question-text">
                          {item.faq}
                        </span>
                        <span className="fmw-faq-icon" aria-hidden="true">
                          {isActive ? "−" : "+"}
                        </span>
                      </button>
                    </h3>

                    <div
                      id={answerId}
                      className="fmw-faq-answer"
                      role="region"
                      aria-labelledby={questionId}
                      hidden={!isActive}
                    >
                      <div className="fmw-faq-answer-inner">
                        <HTMLDisplay
                          html={item.faqAns}
                          textNo={100}
                          type="title"
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {totalPages > 1 && (
              <nav className="fmw-faq-pagination" aria-label="FAQ pagination">
                <span className="fmw-faq-pagination-info">
                  Showing {startIndex + 1}–
                  {Math.min(startIndex + faqsPerPage, faqData.length)} of{" "}
                  {faqData.length} FAQs
                </span>

                <div className="fmw-faq-pagination-buttons">
                  <button
                    type="button"
                    className="fmw-faq-page-button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>

                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1,
                  ).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`fmw-faq-page-button ${
                        currentPage === page ? "is-current" : ""
                      }`}
                      onClick={() => goToPage(page)}
                      aria-current={currentPage === page ? "page" : undefined}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="fmw-faq-page-button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </nav>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default FaqSection;
